import { getTextEditorPageLayoutMetrics } from './textEditorPageOrientation';

export const TEXT_EDITOR_PRINT_MARGIN_PRESET = {
  DEFAULT: 'default',
  NARROW: 'narrow',
  MODERATE: 'moderate',
  WIDE: 'wide',
  CUSTOM: 'custom',
};

export const TEXT_EDITOR_DEFAULT_PRINT_MARGIN_PRESET = TEXT_EDITOR_PRINT_MARGIN_PRESET.DEFAULT;

const PRESET_MARGINS_IN = {
  [TEXT_EDITOR_PRINT_MARGIN_PRESET.DEFAULT]: {
    top: 1,
    right: 1,
    bottom: 1,
    bottomWithPageNumbers: 1.15,
    left: 1,
  },
  [TEXT_EDITOR_PRINT_MARGIN_PRESET.NARROW]: {
    top: 0.5,
    right: 0.5,
    bottom: 0.5,
    bottomWithPageNumbers: 0.65,
    left: 0.5,
  },
  [TEXT_EDITOR_PRINT_MARGIN_PRESET.MODERATE]: {
    top: 0.75,
    right: 0.75,
    bottom: 0.75,
    bottomWithPageNumbers: 0.9,
    left: 0.75,
  },
  [TEXT_EDITOR_PRINT_MARGIN_PRESET.WIDE]: {
    top: 1.25,
    right: 1.25,
    bottom: 1.25,
    bottomWithPageNumbers: 1.4,
    left: 1.25,
  },
};

export const TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN = {
  top: 1,
  right: 1,
  bottom: 1.15,
  left: 1,
};

export function normalizePrintMarginPreset(value) {
  const key = String(value || '').toLowerCase();
  if (Object.values(TEXT_EDITOR_PRINT_MARGIN_PRESET).includes(key)) return key;
  return TEXT_EDITOR_DEFAULT_PRINT_MARGIN_PRESET;
}

export function clampPrintMarginInches(value, fallback = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(2, Math.max(0.25, Math.round(n * 100) / 100));
}

export function normalizeCustomPrintMarginsIn(input) {
  const src = input && typeof input === 'object' ? input : {};
  return {
    top: clampPrintMarginInches(src.top, TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN.top),
    right: clampPrintMarginInches(src.right, TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN.right),
    bottom: clampPrintMarginInches(src.bottom, TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN.bottom),
    left: clampPrintMarginInches(src.left, TEXT_EDITOR_DEFAULT_CUSTOM_PRINT_MARGINS_IN.left),
  };
}

function inchToCss(inches) {
  return `${inches}in`;
}

function inchToPx(inches) {
  return Math.round(inches * 96);
}

function inchToPt(inches) {
  return inches * 72;
}

export function resolveTextEditorPrintMargins({
  preset,
  customMarginsIn,
  pageNumbers,
} = {}) {
  const resolvedPreset = normalizePrintMarginPreset(preset);
  let topIn;
  let rightIn;
  let bottomIn;
  let leftIn;

  if (resolvedPreset === TEXT_EDITOR_PRINT_MARGIN_PRESET.CUSTOM) {
    const custom = normalizeCustomPrintMarginsIn(customMarginsIn);
    topIn = custom.top;
    rightIn = custom.right;
    leftIn = custom.left;
    bottomIn = custom.bottom;
    if (pageNumbers && bottomIn < topIn + 0.12) {
      bottomIn = Math.max(bottomIn, topIn + 0.15);
    }
  } else {
    const block = PRESET_MARGINS_IN[resolvedPreset] || PRESET_MARGINS_IN.default;
    topIn = block.top;
    rightIn = block.right;
    leftIn = block.left;
    bottomIn = pageNumbers ? block.bottomWithPageNumbers : block.bottom;
  }

  return {
    preset: resolvedPreset,
    topIn,
    rightIn,
    bottomIn,
    leftIn,
    topCss: inchToCss(topIn),
    rightCss: inchToCss(rightIn),
    bottomCss: inchToCss(bottomIn),
    leftCss: inchToCss(leftIn),
    topPx: inchToPx(topIn),
    rightPx: inchToPx(rightIn),
    bottomPx: inchToPx(bottomIn),
    leftPx: inchToPx(leftIn),
    topPt: inchToPt(topIn),
    rightPt: inchToPt(rightIn),
    bottomPt: inchToPt(bottomIn),
    leftPt: inchToPt(leftIn),
  };
}

export function getTextEditorPrintMarginStyleProperties(margins) {
  const m = margins || resolveTextEditorPrintMargins({});
  return {
    '--wfm-print-margin-top': m.topCss,
    '--wfm-print-margin-right': m.rightCss,
    '--wfm-print-margin-bottom': m.bottomCss,
    '--wfm-print-margin-left': m.leftCss,
  };
}

export function formatPrintMarginsSummary(margins) {
  const m = margins || resolveTextEditorPrintMargins({});
  return `${m.topIn}" top · ${m.rightIn}" right · ${m.bottomIn}" bottom · ${m.leftIn}" left`;
}

export function buildExportPrintLayoutCss(pageOrientation, pageNumbers, margins) {
  const metrics = getTextEditorPageLayoutMetrics(pageOrientation);
  const m = margins || resolveTextEditorPrintMargins({ pageNumbers });
  return `
.wfm-export-print-layout {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  margin: 0;
  padding: ${m.topCss} ${m.rightCss} ${m.bottomCss} ${m.leftCss};
}
@media print {
  @page {
    size: ${metrics.wordPageSizeCss};
    margin: ${m.topCss} ${m.rightCss} ${m.bottomCss} ${m.leftCss};
  }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #ffffff !important;
  }
  .wfm-export-print-layout {
    padding: 0 !important;
    max-width: none !important;
  }
}
`;
}

export function wrapExportBodyForPrintLayout(innerHtml) {
  return `<div class="wfm-export-print-layout">${innerHtml || ''}</div>`;
}

export function buildWordSectionPrintPaddingCss(margins) {
  const m = margins || resolveTextEditorPrintMargins({});
  return `
div.Section1 {
  padding: ${m.topCss} ${m.rightCss} ${m.bottomCss} ${m.leftCss};
  box-sizing: border-box;
}
`;
}

/** @deprecated use resolveTextEditorPrintMargins — kept for imports that expect default px */
export const TEXT_EDITOR_PRINT_MARGIN_IN = 1;
export const TEXT_EDITOR_PRINT_MARGIN_BOTTOM_IN = 1.15;
